"use client";

import React, { ReactNode } from "react";
import { icons } from "@/icons";
import HeroParallaxMotion from "./HeroParallaxMotion";

interface HeroParallaxBaseProps {
  children: ReactNode;
  /** Optional: Hide the insurance disclaimer (if your content includes its own) */
  hideDisclaimer?: boolean;
}

/**
 * HeroParallaxBase - Pure parallax background component.
 *
 * This component provides ONLY the parallax island/ocean background.
 * Content (headlines, cards, phones, etc.) is passed as children and
 * rendered above the parallax layer.
 *
 * Container heights:
 * - Mobile: 880px
 * - Desktop (lg+): 900px
 *
 * Each hero concept is responsible for its own phone mockups, CTAs, etc.
 */
export default function HeroParallaxBase({
  children,
  hideDisclaimer = false
}: HeroParallaxBaseProps) {
  return (
    <section id="herosection" className="relative pt-24">
      {/* Background gradient - full section coverage */}
      <div className="absolute inset-0 bg-custom-gradient" />
      <icons.lightarrays className="w-full absolute mt-[-20rem] md:mt-[-15rem] xl:mt-[-9rem]" />

      {/*
        Parallax container - ABSOLUTELY POSITIONED with fixed dimensions.
        Starts at top-32 to align with section's pt-32 padding.
        This sits behind the content and doesn't affect document flow.
        The parallax elements position themselves relative to this fixed container.
      */}
      <div className="absolute top-24 left-0 right-0 h-[880px] lg:h-[900px] pointer-events-none">
        {/* Parallax blur overlay - exact position from original */}
        <div className="absolute bg-white/5 top-[33%] blur-[74px] backdrop-blur-[44px] w-[110%] h-[8.875rem] left-[-1.875rem]" />

        {/* Parallax motion elements */}
        <HeroParallaxMotion />
      </div>

      {/*
        Content layer - NORMAL DOCUMENT FLOW
        This determines the actual section height and pushes down subsequent sections.
        z-30 keeps content above parallax elements.
      */}
      <div className="relative z-30">
        {children}

        {/* Insurance disclaimer - positioned at bottom of content area */}
        {!hideDisclaimer && (
          <div className="mt-5 flex flex-col items-center gap-4">
            <p className="font-semibold text-sm px-6 xl:px-0 text-white text-center">
              Cove is not a bank. Please see our footer for *, ** disclaimers and more information.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
